/**
 * Onboarding Wizard — professional guided tour for first-time users.
 *
 * Design for 55-year-old lawyer:
 *   - Max 2 sentences per step (no jargon)
 *   - Each step shows concrete benefit ("oszczedzisz 2 godziny")
 *   - Visual icon + progress dots
 *   - Skip available (but soft — "Pokaż później")
 *   - Completed in <60 seconds (5 steps × 10s each)
 *   - Only appears ONCE (localStorage flag)
 *   - Can be reset from Settings
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  FileText, MessageSquare, FolderOpen, Search, Shield,
  ChevronRight, ChevronLeft, X, Sparkles, Check, Scale,
  Upload, Clock,
} from "lucide-react";

const STEPS = [
  {
    icon: Sparkles,
    color: "bg-blue-100 text-blue-600",
    title: "Witaj w LES-LEX!",
    description: "System, który przygotuje pismo procesowe w 60 sekund zamiast 2 godzin. Pokaże Ci jak — w 5 krokach.",
    benefit: null,
  },
  {
    icon: FolderOpen,
    color: "bg-green-100 text-green-600",
    title: "1. Utwórz sprawę",
    description: "Kliknij \"Nowa sprawa\" na Pulpicie. Wybierz typ (cywilna, karna, rodzinna...) i opisz krótko problem.",
    benefit: "Wszystkie dokumenty, notatki i rozmowy z AI w jednym miejscu",
  },
  {
    icon: Upload,
    color: "bg-amber-100 text-amber-600",
    title: "2. Naucz AI pisać jak Ty",
    description: "Wejdź w Ustawienia → Styl pisania. Wgraj 5-10 swoich pism — AI przeanalizuje Twój styl: słownictwo, strukturę, ton. Od teraz będzie pisał jak TY.",
    benefit: "Klient nie odróżni pisma AI od Twojego — ten sam styl kancelarii",
  },
  {
    icon: FolderOpen,
    color: "bg-orange-100 text-orange-600",
    title: "3. Dodaj materiały do sprawy",
    description: "Wrzuć dokumenty klienta (PDF, Word, zdjęcia). System automatycznie odczyta treść — nawet ze skanów.",
    benefit: "AI przeczyta akta za Ciebie i wykorzysta je w pismie",
  },
  {
    icon: FileText,
    color: "bg-purple-100 text-purple-600",
    title: "4. Wygeneruj pismo",
    description: "Przejdź do Kreatora, wybierz szablon (pozew, apelacja, wniosek...) i kliknij \"Generuj\". AI napisze pismo z przepisami i orzecznictwem — w Twoim stylu.",
    benefit: "Gotowe pismo w 60 sekund — w stylu Twojej kancelarii",
  },
  {
    icon: MessageSquare,
    color: "bg-cyan-100 text-cyan-600",
    title: "5. Dopytaj AI w Studio",
    description: "Nie jesteś pewien argumentacji? Wejdź do Studio i zapytaj AI — odpowie na podstawie kodeksów, komentarzy i orzeczeń sądowych.",
    benefit: "Jak rozmowa z doświadczonym kolegą po fachu",
  },
  {
    icon: Check,
    color: "bg-emerald-100 text-emerald-600",
    title: "Gotowe!",
    description: "Znasz podstawy. System ma też: analizę umów, porady podatkowe, wyszukiwanie orzeczeń i wiele więcej. Odkryjesz je w bocznym menu.",
    benefit: null,
  },
];

const STORAGE_KEY = "leslex_onboarding_v2";

export default function OnboardingWizard() {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const complete = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setVisible(false);
  };

  const skip = () => {
    localStorage.setItem(STORAGE_KEY, "later");
    setVisible(false);
  };

  if (!visible) return null;

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <>
      {/* Dark overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity duration-300"
        onClick={skip}
      />

      {/* Wizard card */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101]
                      bg-white dark:bg-slate-900 rounded-2xl shadow-2xl
                      w-[460px] max-w-[92vw] overflow-hidden
                      animate-in fade-in zoom-in-95 duration-300">

        {/* Progress bar */}
        <div className="h-1.5 bg-gray-100 dark:bg-gray-800">
          <div
            className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Skip button */}
        <button
          onClick={skip}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
          title="Pokaż później"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Content */}
        <div className="px-8 pt-8 pb-4 text-center">
          {/* Icon circle */}
          <div className={`w-18 h-18 rounded-2xl ${s.color} flex items-center justify-center mx-auto mb-5 p-4`}>
            <Icon className="h-9 w-9" />
          </div>

          {/* Step indicator */}
          {step > 0 && step < STEPS.length - 1 && (
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Krok {step} z {STEPS.length - 2}
            </p>
          )}

          {/* Title */}
          <h2 className="text-xl font-bold mb-3 text-foreground">{s.title}</h2>

          {/* Description */}
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            {s.description}
          </p>

          {/* Benefit badge */}
          {s.benefit && (
            <div className="inline-flex items-center gap-1.5 bg-primary/5 border border-primary/20 rounded-full px-4 py-1.5 mb-2">
              <Clock className="h-3 w-3 text-primary" />
              <span className="text-xs font-medium text-primary">{s.benefit}</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="px-8 pb-5 flex items-center justify-between">
          {/* Back button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStep(step - 1)}
            className={step === 0 ? "invisible" : ""}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Wstecz
          </Button>

          {/* Progress dots */}
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === step ? "bg-primary w-6" : i < step ? "bg-primary/30 w-2" : "bg-gray-200 dark:bg-gray-700 w-2"
                }`}
              />
            ))}
          </div>

          {/* Next / Complete button */}
          <Button size="sm" onClick={isLast ? complete : () => setStep(step + 1)}>
            {isLast ? (
              <><Check className="h-4 w-4 mr-1" /> Zaczynamy!</>
            ) : (
              <>Dalej <ChevronRight className="h-4 w-4 ml-1" /></>
            )}
          </Button>
        </div>

        {/* Skip link (soft) */}
        {!isLast && (
          <div className="text-center pb-4">
            <button onClick={skip} className="text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors">
              Pokaż później
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/** Reset onboarding — used in Settings page */
export function resetOnboarding() {
  localStorage.removeItem(STORAGE_KEY);
  window.location.reload();
}

/** Check if completed */
export function isOnboardingCompleted() {
  return !!localStorage.getItem(STORAGE_KEY);
}
